/**
 * #/match/:id — el detalle del partido.
 *
 * Dos orígenes de datos, a propósito:
 *  - **`GET /report`** para la pestaña Resumen: es la proyección oficial del
 *    API (la misma que imprime el reporte), no se recalcula acá.
 *  - **`GET /events`** para Rotaciones, Armador, Zonas y Evolución: el portal
 *    se baja el log una vez y las deriva en el cliente (ver
 *    `src/lib/projections.ts`, y en `src/lib/log.ts` por qué no importa
 *    `@scout/stats` ni pide todavía `/projections/:id`, que el API ya publica).
 *
 * El selector de sets re-pide el reporte con `?sets=` y refiltra el log; el
 * criterio de "qué set es cada evento" es el mismo de los dos lados.
 *
 * PERMISOS: los dos orígenes NO piden lo mismo. `/events` exige `events:read`,
 * que el rol `viewer` no tiene, así que esa mitad puede volver 403 mientras el
 * resto anda perfecto. Cuando pasa, la página degrada (ver `match/access.ts`):
 * sigue mostrando el resumen y los dos documentos imprimibles, y explica en un
 * aviso —no en un cartel rojo— por qué las otras vistas quedan vacías.
 *
 * DOCUMENTOS: son DOS y distintos. La *planilla* (`scoresheet.html`) es el acta
 * de mesa, del partido entero; el *reporte* (`report.html`) es la estadística y
 * respeta el filtro de sets.
 */
import { useMemo, useState, useSyncExternalStore } from 'react';
import type { MatchReport, TeamSide } from '@scout/core';
import type { MessageKey, TFunction } from '../i18n/index';
import { useT } from '../i18n/use-t';
import { Card, Empty, ErrorBox, Loading, Notice } from '../components/States';
import { openHtmlInTab, saveTextAs } from '../browser';
import { errorMessage, useAsync, type AsyncResult } from '../hooks';
import type { ApiClient, MatchDocument } from '../lib/api-client';
import {
  DEFAULT_EFFICIENCY_PRESET,
  getEfficiencyConfig,
  getEfficiencyPreset,
  subscribeEfficiency,
} from '../lib/efficiency';
import {
  PREVIOUS_RALLY_PRESETS,
  previousRallyPreset,
  type PreviousRallyFilter,
  type PreviousRallyPresetId,
} from '../lib/rally-filter';
import {
  normalizeLog,
  readMatchMeta,
  readSetResults,
  setsInLog,
  setsWon,
  type MatchMeta,
} from '../lib/log';
import {
  buildReceptionImpact,
  buildRotationReport,
  buildScoreProgression,
  buildSetterCourt,
  buildSetterDistribution,
  buildZoneMatrix,
  playersWithSkill,
  type ReceptionImpact,
  type RotationReport,
  type ScorePoint,
  type SetterCourt,
  type SetterDistribution,
  type ZoneMatrix,
  type ZoneSkill,
} from '../lib/projections';
import { MATCH_TABS, matchHash, type MatchTab } from '../lib/router';
import { eventAccess, missingPermission, tabNeedsEvents, type EventAccess } from './match/access';
import { ClipsTab } from './match/ClipsTab';
import { EvolutionTab } from './match/EvolutionTab';
import { LiveTab } from './match/LiveTab';
import { RotationsTab } from './match/RotationsTab';
import { SetterTab } from './match/SetterTab';
import { SummaryTab } from './match/SummaryTab';
import { ZonesTab } from './match/ZonesTab';

const TAB_KEYS: Record<MatchTab, MessageKey> = {
  resumen: 'match.tabSummary',
  rotaciones: 'match.tabRotations',
  armador: 'match.tabSetter',
  zonas: 'match.tabZones',
  clips: 'match.tabClips',
  evolucion: 'match.tabEvolution',
  vivo: 'match.tabLive',
};

export function MatchPage({
  client,
  matchId,
  tab,
  replayUrl,
  replayToken,
}: {
  client: ApiClient;
  matchId: string;
  tab: MatchTab;
  /** Server de replay VAR de la cancha (config de este navegador). */
  replayUrl: string;
  replayToken: string;
}): JSX.Element {
  /** `null` = todos los sets. */
  const [set, setSet] = useState<number | null>(null);
  /** Fundamento del mapa de zonas: ataque (default), saque o recepción. */
  const [zoneSkill, setZoneSkill] = useState<ZoneSkill>('attack');
  const [homePlayer, setHomePlayer] = useState<number | null>(null);
  const [awayPlayer, setAwayPlayer] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** "One Rally Before": preset elegido y desde qué equipo se lee ("propio"). */
  const [prevPreset, setPrevPreset] = useState<PreviousRallyPresetId | ''>('');
  const [prevTeam, setPrevTeam] = useState<TeamSide>('home');
  const t = useT();

  // Convención de eficiencia activa (se elige en Ajustes; acá sólo se muestra
  // y se respeta). Es un store de módulo, igual que el idioma.
  const effPreset = useSyncExternalStore(subscribeEfficiency, getEfficiencyPreset, getEfficiencyPreset);
  const effConfig = useMemo(() => getEfficiencyConfig(), [effPreset]);
  /** Al API sólo viaja si no es el default: menos query y misma respuesta. */
  const effParam = effPreset === DEFAULT_EFFICIENCY_PRESET ? undefined : effPreset;

  const sets = useMemo(() => (set === null ? undefined : [set]), [set]);
  const previousRally: PreviousRallyFilter | undefined = useMemo(
    () => (prevPreset === '' ? undefined : previousRallyPreset(prevPreset, prevTeam)),
    [prevPreset, prevTeam],
  );

  /**
   * Cambiar de fundamento limpia el filtro de camiseta: el #14 que remata
   * puede no sacar nunca, y arrastrar el filtro dejaría la matriz vacía sin
   * que se entienda por qué.
   */
  const changeZoneSkill = (value: ZoneSkill): void => {
    setZoneSkill(value);
    setHomePlayer(null);
    setAwayPlayer(null);
  };

  const events = useAsync(() => client.getEvents(matchId), [client, matchId]);
  const report = useAsync(
    () => client.getMatchReport(matchId, sets, { efficiency: effParam, previousRally }),
    [client, matchId, set, effPreset, prevPreset, prevTeam],
  );

  const derived = useMemo(() => {
    const log = normalizeLog(events.data?.events ?? []);
    const meta = readMatchMeta(log, matchId);
    const results = readSetResults(log);
    const efficiency = effConfig;
    return {
      log,
      meta,
      results,
      won: setsWon(results),
      available: setsInLog(log),
      // La rotación y la evolución no toman ni convención ni "One Rally
      // Before": son métricas de rally, no divisiones de evaluaciones (mismo
      // alcance que en `@scout/stats`).
      rotation: buildRotationReport(log, { sets }),
      setter: buildSetterDistribution(log, { sets, efficiency, previousRally }),
      // La misma muestra, agrupada por zona de salida: es lo que se dibuja sobre la cancha.
      setterCourt: buildSetterCourt(log, { sets, efficiency, previousRally }),
      reception: buildReceptionImpact(log, { sets, previousRally }),
      // Una matriz por equipo: el filtro de camiseta es independiente por lado.
      // El fundamento sí es común a los dos (un "mapa de saque" es del partido).
      zonesHome: buildZoneMatrix(log, {
        sets,
        efficiency,
        previousRally,
        skill: zoneSkill,
        player: homePlayer ?? undefined,
      }),
      zonesAway: buildZoneMatrix(log, {
        sets,
        efficiency,
        previousRally,
        skill: zoneSkill,
        player: awayPlayer ?? undefined,
      }),
      // El selector de camiseta también depende del fundamento: los que rematan
      // no son los que reciben.
      zonePlayersHome: playersWithSkill(log, 'home', zoneSkill),
      zonePlayersAway: playersWithSkill(log, 'away', zoneSkill),
      score: buildScoreProgression(log, { sets }),
    };
  }, [events.data, matchId, set, zoneSkill, homePlayer, awayPlayer, sets, effConfig, previousRally]);

  /**
   * Los tres documentos del partido. `scoresheet` (acta) no lleva `?sets=`: el
   * acta es siempre del partido completo, como la de papel.
   */
  const download = async (kind: 'dvw' | MatchDocument): Promise<void> => {
    setBusy(kind);
    setNotice(null);
    try {
      if (kind === 'dvw') {
        const file = await client.downloadDvw(matchId);
        saveTextAs(`${matchId}.dvw`, file.body, file.contentType);
      } else {
        // El imprimible respeta la convención pero NO el "One Rally Before":
        // es el documento oficial del recorte de sets, no de un cruce.
        const file = await client.downloadMatchDocument(matchId, kind, sets, effParam);
        openHtmlInTab(file.body);
      }
    } catch (err) {
      setNotice(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(null);
    }
  };

  const access = eventAccess(events.error);
  // Sin log, `readMatchMeta` rellena con «Local»/«Visitante»; el reporte —que
  // el rol viewer SÍ puede pedir— trae los nombres de verdad.
  const fromReport = derived.log.length === 0 ? report.data : null;
  const names = {
    home: fromReport?.home.name || derived.meta.teams.home.name,
    away: fromReport?.away.name || derived.meta.teams.away.name,
  };
  const title = derived.meta.matchName || matchId;
  const loadingFirst = events.first && events.status === 'loading';

  const winner =
    access === 'forbidden'
      ? null
      : derived.won.home > derived.won.away
        ? 'home'
        : derived.won.away > derived.won.home
          ? 'away'
          : null;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          <div className="hero-meta">
            <span>{matchId}</span>
            {derived.meta.competition ? <span>{derived.meta.competition}</span> : null}
            {derived.meta.venue ? <span>{derived.meta.venue}</span> : null}
          </div>
        </div>
        <div className="row">
          <button
            type="button"
            disabled={busy !== null}
            title={t('matches.scoresheetTitle')}
            onClick={() => void download('scoresheet')}
          >
            {busy === 'scoresheet' ? '…' : t('match.printable')}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            title={t('matches.reportTitle')}
            onClick={() => void download('report')}
          >
            {busy === 'report' ? '…' : t('match.report')}
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy !== null}
            onClick={() => void download('dvw')}
          >
            {busy === 'dvw' ? '…' : t('match.downloadDvw')}
          </button>
        </div>
      </div>

      {/* El marcador del partido: los dos equipos y los sets al medio, con los
          parciales abajo. Es la cabecera de todas las pestañas. */}
      <section className="card match-hero">
        <div className="match-head">
          <div className="sets-board">
            <span className={`hero-team home side-name${winner === 'home' ? ' won' : ''}`}>
              {names.home}
            </span>
            <span className="hero-center">
              <span className="hero-label">{t('common.sets')}</span>
              {/* Sin log no se sabe el resultado: mejor un guión que un 0–0 falso. */}
              <span
                className="sets-score"
                title={access === 'forbidden' ? t('access.noScoreboard') : undefined}
              >
                {access === 'forbidden' ? '—' : `${derived.won.home}–${derived.won.away}`}
              </span>
            </span>
            <span className={`hero-team away side-name${winner === 'away' ? ' won' : ''}`}>
              {names.away}
            </span>
          </div>
        </div>
        <div className="set-pills hero-partials">
          {access === 'forbidden' ? (
            <span className="hint">{t('access.noScoreboard')}</span>
          ) : derived.results.length === 0 ? (
            <span className="hint">{t('match.noClosedSets')}</span>
          ) : (
            derived.results.map((result) => (
              <span key={result.setNumber} className="set-pill">
                S{result.setNumber}{' '}
                <b className={result.home > result.away ? 'win' : undefined}>{result.home}</b>–
                <b className={result.away > result.home ? 'win' : undefined}>{result.away}</b>
              </span>
            ))
          )}
        </div>
      </section>

      {/* Recortes de la PÁGINA: sets y "One Rally Before" aplican a Resumen,
          Armador, Zonas y Clips a la vez, así ninguna pestaña muestra un número
          de otro cruce. La convención de eficiencia se elige en Ajustes. */}
      <div className="toolbar">
        <label className="row" style={{ gap: 6 }}>
          <span className="hint">{t('common.sets')}</span>
          <select
            value={set === null ? '' : String(set)}
            onChange={(e) => setSet(e.target.value === '' ? null : Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value="">{t('common.all')}</option>
            {(derived.available.length > 0 ? derived.available : [1, 2, 3, 4, 5]).map((n) => (
              <option key={n} value={n}>
                {t('common.set', { set: n })}
              </option>
            ))}
          </select>
        </label>
        <label className="row" style={{ gap: 6 }}>
          <span className="hint">{t('prevRally.filterLabel')}</span>
          <select
            value={prevPreset}
            onChange={(e) => setPrevPreset(e.target.value as PreviousRallyPresetId | '')}
            style={{ width: 'auto' }}
          >
            <option value="">{t('prevRally.none')}</option>
            {PREVIOUS_RALLY_PRESETS.map((id) => (
              <option key={id} value={id}>
                {t(`prevRally.preset_${id}` as MessageKey)}
              </option>
            ))}
          </select>
        </label>
        {prevPreset !== '' ? (
          <label className="row" style={{ gap: 6 }}>
            <span className="hint">{t('prevRally.teamLabel')}</span>
            <select
              value={prevTeam}
              onChange={(e) => setPrevTeam(e.target.value as TeamSide)}
              style={{ width: 'auto' }}
            >
              <option value="home">{names.home}</option>
              <option value="away">{names.away}</option>
            </select>
          </label>
        ) : null}
        <span className="spacer" />
        <span className="chip muted" title={t('efficiency.chipTitle')}>
          {t('efficiency.chip', { name: effConfig.name })}
        </span>
      </div>
      {prevPreset !== '' ? (
        <p className="hint" style={{ marginTop: 0 }}>
          {t('prevRally.activeHint')}
        </p>
      ) : null}

      {notice ? <ErrorBox message={notice} /> : null}

      {access === 'forbidden' ? (
        <Notice
          message={t('access.noEvents')}
          hint={t('access.noEventsHint', { permission: missingPermission(events.error) })}
          action={
            tab === 'resumen' ? undefined : (
              <a href={matchHash(matchId, 'resumen')} style={{ textDecoration: 'none' }}>
                <button type="button" className="small">
                  {t('access.goToSummary')}
                </button>
              </a>
            )
          }
        />
      ) : null}

      {/* Las pestañas sin permiso se dejan navegables (explican por qué al
          entrar), pero el title ya lo adelanta al pasar el mouse. */}
      <nav className="tabs">
        {MATCH_TABS.map((name) => {
          const blocked = access === 'forbidden' && tabNeedsEvents(name);
          return (
            <a key={name} href={matchHash(matchId, name)} style={{ textDecoration: 'none' }}>
              <button
                type="button"
                className={`tab${tab === name ? ' active' : ''}`}
                title={blocked ? t('access.noEventsTab') : undefined}
              >
                {t(TAB_KEYS[name])}
              </button>
            </a>
          );
        })}
      </nav>

      {/* El 403 ya se explicó arriba con el aviso: acá sólo lo que se reintenta. */}
      {access === 'error' && tab !== 'resumen' ? (
        <ErrorBox message={errorMessage(events.error)} onRetry={events.reload} />
      ) : null}

      {loadingFirst && tab !== 'vivo' ? <Loading what={t('match.loadingWhat')} /> : null}

      {!loadingFirst || tab === 'vivo' ? (
        <TabContent
          tab={tab}
          client={client}
          matchId={matchId}
          derived={derived}
          report={report}
          access={access}
          sets={sets}
          previousRally={previousRally}
          replayUrl={replayUrl}
          replayToken={replayToken}
          zoneSkill={zoneSkill}
          onZoneSkill={changeZoneSkill}
          homePlayer={homePlayer}
          awayPlayer={awayPlayer}
          onHomePlayer={setHomePlayer}
          onAwayPlayer={setAwayPlayer}
          t={t}
        />
      ) : null}
    </div>
  );
}

/**
 * Camisetas de un equipo según el REPORTE. La pestaña Clips las usa en vez de
 * las del log porque el reporte entra con `matches:read` y el log no: así el
 * filtro de camiseta le funciona también al dirigente.
 */
function reportPlayers(report: MatchReport | null, side: 'home' | 'away'): number[] {
  const players = report?.[side].players ?? [];
  return players.map((player) => player.number).sort((a, b) => a - b);
}

/** Lo que el memo de arriba deja listo para las pestañas. */
interface Derived {
  meta: MatchMeta;
  rotation: RotationReport;
  setter: SetterDistribution;
  setterCourt: SetterCourt;
  reception: ReceptionImpact;
  zonesHome: ZoneMatrix;
  zonesAway: ZoneMatrix;
  zonePlayersHome: number[];
  zonePlayersAway: number[];
  score: ScorePoint[];
}

function TabContent({
  tab,
  client,
  matchId,
  derived,
  report,
  access,
  sets,
  previousRally,
  replayUrl,
  replayToken,
  zoneSkill,
  onZoneSkill,
  homePlayer,
  awayPlayer,
  onHomePlayer,
  onAwayPlayer,
  t,
}: {
  tab: MatchTab;
  client: ApiClient;
  matchId: string;
  derived: Derived;
  report: AsyncResult<MatchReport>;
  access: EventAccess;
  sets: number[] | undefined;
  previousRally: PreviousRallyFilter | undefined;
  replayUrl: string;
  replayToken: string;
  zoneSkill: ZoneSkill;
  onZoneSkill: (value: ZoneSkill) => void;
  homePlayer: number | null;
  awayPlayer: number | null;
  onHomePlayer: (value: number | null) => void;
  onAwayPlayer: (value: number | null) => void;
  t: TFunction;
}): JSX.Element {
  // Sin permiso sobre el log, estas vistas no están vacías: están cerradas. Se
  // dice, en vez de dibujar tablas en cero que parecen un partido sin cargar.
  if (access === 'forbidden' && tabNeedsEvents(tab)) {
    return (
      <Card>
        <Empty>{t('access.noEventsTab')}</Empty>
      </Card>
    );
  }

  switch (tab) {
    case 'resumen': {
      if (report.status === 'error') {
        return <ErrorBox message={errorMessage(report.error)} onRetry={report.reload} />;
      }
      if (!report.data) return <Loading what={t('match.loadingReport')} />;
      return <SummaryTab report={report.data} />;
    }
    case 'rotaciones':
      return <RotationsTab report={derived.rotation} />;
    case 'armador':
      return (
        <SetterTab
          distribution={derived.setter}
          reception={derived.reception}
          court={derived.setterCourt}
        />
      );
    case 'zonas':
      return (
        <ZonesTab
          matrix={{ ...derived.zonesHome, away: derived.zonesAway.away }}
          skill={zoneSkill}
          onSkill={onZoneSkill}
          homePlayers={derived.zonePlayersHome}
          awayPlayers={derived.zonePlayersAway}
          homePlayer={homePlayer}
          awayPlayer={awayPlayer}
          onHomePlayer={onHomePlayer}
          onAwayPlayer={onAwayPlayer}
        />
      );
    case 'clips':
      return (
        <ClipsTab
          client={client}
          matchId={matchId}
          sets={sets}
          previousRally={previousRally}
          // Las camisetas salen del REPORTE, no del log: el dirigente (rol
          // `viewer`) no tiene `events:read` y la playlist sí la puede pedir.
          homePlayers={reportPlayers(report.data, 'home')}
          awayPlayers={reportPlayers(report.data, 'away')}
          homeName={report.data?.home.name || derived.meta.teams.home.name}
          awayName={report.data?.away.name || derived.meta.teams.away.name}
          replayUrl={replayUrl}
          replayToken={replayToken}
        />
      );
    case 'evolucion':
      return (
        <EvolutionTab
          score={derived.score}
          homeName={derived.meta.teams.home.name}
          awayName={derived.meta.teams.away.name}
        />
      );
    case 'vivo':
      return <LiveTab client={client} matchId={matchId} />;
    default:
      return (
        <Card>
          <Empty>{t('routes.unknownTab')}</Empty>
        </Card>
      );
  }
}
